'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  MagnifyingGlassIcon,
  BookmarkIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  MegaphoneIcon,
  FireIcon,
  ArrowTopRightOnSquareIcon,
  GlobeAltIcon,
  ClockIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import AdSlider from '@/components/AdSlider';
import MiniAdCard from '@/components/MiniAdCard';
import { createClient } from '@/lib/local-api/client';
import { watchlistApi, projectsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import SignInPromptModal from '@/components/SignInPromptModal';

const categories = [
  { id: 'cat-crypto', label: 'Crypto', emoji: '₿', color: 'bg-orange-500/20 text-orange-400' },
  { id: 'cat-forex', label: 'Forex', emoji: '📈', color: 'bg-blue-500/20 text-blue-400' },
  { id: 'cat-ai', label: 'AI Projects', emoji: '⚙️', color: 'bg-purple-500/20 text-purple-400' },
  { id: 'cat-invest', label: 'Investments', emoji: '💰', color: 'bg-green-500/20 text-green-400' },
  { id: 'cat-defi', label: 'DeFi', emoji: '🔗', color: 'bg-cyan-500/20 text-cyan-400' },
  { id: 'cat-online', label: 'Online Earning', emoji: '💻', color: 'bg-pink-500/20 text-pink-400' },
  { id: 'cat-biz', label: 'Business', emoji: '🏢', color: 'bg-yellow-500/20 text-yellow-400' },
  { id: 'cat-all', label: 'All Categories', emoji: '⊞', color: 'bg-muted text-muted-foreground' },
];

const categoryMap: Record<string, string> = {
  'cat-crypto': 'Crypto',
  'cat-forex': 'Forex',
  'cat-ai': 'AI Project',
  'cat-invest': 'Investment',
  'cat-defi': 'DeFi',
  'cat-online': 'Online Earning',
  'cat-biz': 'Business',
};

const categoryBg: Record<string, string> = {
  Crypto: 'bg-blue-700',
  Forex: 'bg-green-700',
  'AI Project': 'bg-purple-700',
  DeFi: 'bg-indigo-700',
  Investment: 'bg-teal-700',
  NFT: 'bg-pink-700',
  Web3: 'bg-violet-700',
  Business: 'bg-yellow-700',
  'Online Earning': 'bg-pink-700',
};

const riskLevels = ['All', 'Low', 'Moderate', 'High', 'Critical'];
const industries = ['All Industries', 'Crypto', 'Forex', 'DeFi', 'AI', 'Online Earning'];
const countries = ['All Countries', 'USA', 'UK', 'Singapore', 'UAE', 'Australia'];
const sortOptions = ['Most Popular', 'Highest Rated', 'Newest', 'Most Reviewed'];

interface Project {
  id: string;
  name: string;
  category: string;
  logo_url: string | null;
  website_url: string | null;
  rating: number;
  risk_level: string;
  created_at?: string;
}

interface UpcomingProject {
  id: string;
  name: string;
  category: string;
  logo_url: string | null;
  website_url: string | null;
  description: string | null;
  launch_date: string | null;
  is_featured_upcoming: boolean;
}

const riskBgColor = (risk: string) => {
  if (risk === 'Low Risk') return 'bg-green-500/15 text-green-400';
  if (risk === 'Moderate') return 'bg-yellow-500/15 text-yellow-400';
  if (risk === 'High Risk') return 'bg-red-500/15 text-red-400';
  return 'bg-muted text-muted-foreground';
};

export default function ExploreContent() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('cat-all');
  const [riskFilter, setRiskFilter] = useState('All');
  const [industryFilter, setIndustryFilter] = useState('All Industries');
  const [countryFilter, setCountryFilter] = useState('All Countries');
  const [sortBy, setSortBy] = useState('Most Popular');
  const [savedProjects, setSavedProjects] = useState<Set<string>>(new Set());
  const [savingProject, setSavingProject] = useState<string | null>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [upcomingProjects, setUpcomingProjects] = useState<UpcomingProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    projectsApi.list({ status: 'approved' }).then(({ data }) => {
      if (data) {
        const rows = Array.isArray(data) ? data : (data as any)?.data ?? [];
        setAllProjects(rows as Project[]);
      }
    });
    // Upcoming projects still use Supabase directly as projectsApi doesn't filter by status=upcoming
    const supabase = createClient();
    supabase
      .from('projects')
      .select('id, name, category, logo_url, website_url, description, launch_date, is_featured_upcoming')
      .eq('status', 'upcoming')
      .order('launch_date', { ascending: true })
      .limit(8)
      .then(({ data }) => {
        if (data) setUpcomingProjects(data as UpcomingProject[]);
        setLoading(false);
      });
  }, []);

  // Load saved projects from watchlist API
  useEffect(() => {
    if (!user) { setSavedProjects(new Set()); return; }
    watchlistApi.list().then(({ data }) => {
      if (data) {
        const rows = Array.isArray(data) ? data : (data as any)?.data ?? [];
        setSavedProjects(new Set(rows.map((r: any) => r.project_id)));
      }
    });
  }, [user]);

  const handleToggleSave = async (e: React.MouseEvent, projectId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { setShowSignInModal(true); return; }
    if (savingProject === projectId) return;
    setSavingProject(projectId);
    const isSaved = savedProjects.has(projectId);
    if (isSaved) {
      await watchlistApi.remove(projectId);
      setSavedProjects((prev) => { const next = new Set(prev); next.delete(projectId); return next; });
    } else {
      await watchlistApi.add(projectId);
      setSavedProjects((prev) => new Set([...prev, projectId]));
    }
    setSavingProject(null);
  };

  const trendingProjects = [...allProjects]
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, 6);

  const filtered = allProjects.filter((p) => {
    const matchSearch =
      search === '' ||
      p?.name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      p?.category?.toLowerCase()?.includes(search?.toLowerCase());
    const matchRisk =
      riskFilter === 'All' || p?.risk_level?.toLowerCase()?.includes(riskFilter?.toLowerCase());
    const matchCategory =
      activeCategory === 'cat-all' ||
      p?.category?.toLowerCase() === (categoryMap[activeCategory] || '').toLowerCase();
    return matchSearch && matchRisk && matchCategory;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'Highest Rated') return (b.rating || 0) - (a.rating || 0);
    if (sortBy === 'Newest') return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    return (b.rating || 0) - (a.rating || 0);
  });

  return (
    <div className="space-y-5 pb-4">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <h1 className="text-lg font-bold text-foreground">Explore</h1>
        <Link
          href="/place-ad?slot=explore_banner"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors"
        >
          <MegaphoneIcon style={{ width: 13, height: 13 }} />
          Advertise
        </Link>
      </div>
      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          style={{ width: 16, height: 16 }}
        />
        <input
          type="text"
          placeholder="Search projects, companies, platforms..."
          value={search}
          onChange={(e) => setSearch(e?.target?.value)}
          className="w-full bg-card border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
        />
      </div>

      {/* Slot 1 — Top Banner Ad (full banner, largest) */}
      <AdSlider placementSlot="explore_banner" variant="banner" />

      {/* Categories */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-foreground">Categories</h2>
          <button
            onClick={() => setActiveCategory('cat-all')}
            className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"
          >
            View All <ChevronRightIcon style={{ width: 12, height: 12 }} />
          </button>
        </div>
        <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
          {categories?.map((cat) => (
            <button
              key={cat?.id}
              onClick={() => setActiveCategory(cat?.id)}
              className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border transition-all active:scale-95 ${
                activeCategory === cat?.id
                  ? 'border-primary/50 bg-primary/10' : 'border-border bg-card hover:border-primary/30'
              }`}
            >
              <div className={`w-9 h-9 rounded-xl ${cat?.color} flex items-center justify-center text-base`}>
                {cat?.emoji}
              </div>
              <span className="text-[9px] font-medium text-muted-foreground text-center leading-tight">{cat?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Slot 2 — After categories: pill ad (slim, horizontal) */}
      <MiniAdCard placementSlot="explore_mini" variant="pill" />

      {/* Filters */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <AdjustmentsHorizontalIcon style={{ width: 14, height: 14 }} className="text-muted-foreground" />
            <h2 className="text-sm font-bold text-foreground">Filters</h2>
          </div>
          <button
            onClick={() => { setRiskFilter('All'); setIndustryFilter('All Industries'); setCountryFilter('All Countries'); setSortBy('Most Popular'); }}
            className="text-xs text-primary font-semibold hover:underline"
          >
            Reset
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e?.target?.value)}
            className="bg-card border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {riskLevels?.map((r) => <option key={r} value={r}>Risk Level: {r}</option>)}
          </select>
          <select
            value={industryFilter}
            onChange={(e) => setIndustryFilter(e?.target?.value)}
            className="bg-card border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {industries?.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e?.target?.value)}
            className="bg-card border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {countries?.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="bg-card border border-border rounded-lg px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {sortOptions?.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Upcoming Projects */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-lg bg-violet-500/15 flex items-center justify-center">
              <ClockIcon style={{ width: 13, height: 13 }} className="text-violet-400" />
            </div>
            <h2 className="text-sm font-bold text-foreground">Upcoming Projects</h2>
          </div>
          <span className="text-xs text-muted-foreground">Coming Soon</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-5 text-xs text-muted-foreground">Loading...</div>
        ) : upcomingProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 gap-2 bg-card border border-border rounded-2xl border-dashed">
            <ClockIcon style={{ width: 28, height: 28 }} className="text-muted-foreground/40" />
            <p className="text-sm font-medium text-muted-foreground">No upcoming projects yet</p>
            <p className="text-xs text-muted-foreground/60">Check back soon for new launches</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {upcomingProjects.map((project) => (
              <div
                key={project.id}
                className="relative flex items-start gap-3 p-3.5 bg-card border border-border rounded-xl hover:border-violet-500/30 transition-all overflow-hidden group"
              >
                {/* Featured badge */}
                {project.is_featured_upcoming && (
                  <div className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-[9px] font-bold">
                    <SparklesIcon style={{ width: 9, height: 9 }} />
                    Featured
                  </div>
                )}
                {/* Logo */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden ${
                  project.logo_url ? '' : (categoryBg[project.category] || 'bg-violet-700')
                }`}>
                  {project.logo_url ? (
                    <img src={project.logo_url} alt={`${project.name} logo`} className="w-full h-full object-cover" />
                  ) : (
                    <span>{project.name?.slice(0, 2)?.toUpperCase()}</span>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0 pr-12">
                  <p className="text-sm font-semibold text-foreground truncate">{project.name}</p>
                  <p className="text-[11px] text-muted-foreground mb-1">{project.category}</p>
                  {project.description && (
                    <p className="text-[11px] text-muted-foreground/80 line-clamp-2 leading-relaxed">{project.description}</p>
                  )}
                  {project.launch_date && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <ClockIcon style={{ width: 10, height: 10 }} className="text-violet-400" />
                      <span className="text-[10px] font-semibold text-violet-400">
                        Launches {new Date(project.launch_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                </div>
                {/* Visit link */}
                {project.website_url && (
                  <a
                    href={project.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-3 right-3 p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    title="Visit website"
                  >
                    <ArrowTopRightOnSquareIcon style={{ width: 13, height: 13 }} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Trending Projects */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <FireIcon style={{ width: 15, height: 15 }} className="text-orange-400" />
            <h2 className="text-sm font-bold text-foreground">Trending Projects</h2>
          </div>
          <span className="text-xs text-muted-foreground">{trendingProjects?.length} trending</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">Loading...</div>
        ) : trendingProjects.length === 0 ? (
          <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
            <GlobeAltIcon style={{ width: 16, height: 16 }} className="mr-2" />
            No trending projects yet
          </div>
        ) : (
          <div className="space-y-2">
            {trendingProjects?.map((project, index) => (
              <Link
                key={project?.id}
                href={`/project/${project?.id}`}
                onClick={!user ? (e) => { e.preventDefault(); setShowSignInModal(true); } : undefined}
                className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl hover:border-primary/30 transition-all"
              >
                {/* Rank */}
                <span className={`text-xs font-bold w-5 text-center flex-shrink-0 ${index < 3 ? 'text-orange-400' : 'text-muted-foreground'}`}>
                  {index + 1}
                </span>
                {/* Logo/Avatar */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden ${
                  project?.logo_url ? '' : (categoryBg[project?.category] || 'bg-blue-700')
                }`}>
                  {project?.logo_url ? (
                    <img src={project.logo_url} alt={`${project.name} logo`} className="w-full h-full object-cover" />
                  ) : (
                    <span>{project?.name?.slice(0, 2)?.toUpperCase()}</span>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">{project?.name}</p>
                  <p className="text-[11px] text-muted-foreground">{project?.category}</p>
                </div>
                {/* Risk + Rating */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${riskBgColor(project?.risk_level)}`}>
                    {project?.risk_level}
                  </span>
                  <div className="flex items-center gap-0.5">
                    <span className="text-yellow-400 text-xs">★</span>
                    <span className="text-xs font-semibold text-foreground">{project?.rating ?? '—'}</span>
                  </div>
                </div>
                {/* Visit Website */}
                {project?.website_url && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(project.website_url!, '_blank', 'noopener,noreferrer');
                    }}
                    className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex-shrink-0"
                    title="Visit website"
                  >
                    <ArrowTopRightOnSquareIcon style={{ width: 14, height: 14 }} />
                  </button>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Slot 3 — Between trending & all projects: compact slider (medium height) */}
      <AdSlider placementSlot="explore_mid" variant="compact" />

      {/* All Projects */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-foreground">All Projects</h2>
          <span className="text-xs text-muted-foreground">{sorted?.length} results</span>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-xs text-muted-foreground">Loading projects...</div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-2">
            <GlobeAltIcon style={{ width: 28, height: 28 }} />
            <p className="text-sm font-medium">No projects found</p>
            <p className="text-xs">Try adjusting your filters or search</p>
          </div>
        ) : (
          <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
            {sorted?.map((project, index) => (
              <React.Fragment key={project?.id}>
                <Link
                  href={`/project/${project?.id}`}
                  onClick={!user ? (e) => { e.preventDefault(); setShowSignInModal(true); } : undefined}
                  className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl hover:border-primary/30 transition-all"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden ${
                    project?.logo_url ? '' : (categoryBg[project?.category] || 'bg-blue-700')
                  }`}>
                    {project?.logo_url ? (
                      <img src={project.logo_url} alt={`${project.name} logo`} className="w-full h-full object-cover" />
                    ) : (
                      <span>{project?.name?.slice(0, 2)?.toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-semibold text-foreground">{project?.name}</p>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${riskBgColor(project?.risk_level)}`}>
                        {project?.risk_level}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">{project?.category}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <span className="text-yellow-400 text-xs">★</span>
                      <span className="text-xs font-semibold text-foreground">{project?.rating ?? '—'}</span>
                    </div>
                    {project?.website_url ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(project.website_url!, '_blank', 'noopener,noreferrer');
                        }}
                        className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        title="Visit website"
                      >
                        <ArrowTopRightOnSquareIcon style={{ width: 14, height: 14 }} />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={(e) => handleToggleSave(e, project.id)}
                      disabled={savingProject === project.id}
                      className={`p-1.5 rounded-lg transition-colors ${savedProjects?.has(project?.id) ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                      title={savedProjects?.has(project?.id) ? 'Remove from watchlist' : 'Save to watchlist'}
                    >
                      {savingProject === project.id ? (
                        <span className="w-3.5 h-3.5 border border-current/30 border-t-current rounded-full animate-spin block" />
                      ) : (
                        <BookmarkIcon style={{ width: 14, height: 14 }} />
                      )}
                    </button>
                  </div>
                </Link>

                {/* Slot 4 — After 3rd project: MiniAdCard card variant (small square card) */}
                {(index + 1) % 3 === 0 && (index + 1) % 9 !== 0 && (index + 1) % 6 !== 0 && (
                  <div className="lg:col-span-2" key={`ad-card-${index}`}>
                    <MiniAdCard placementSlot="explore_inline_card" variant="card" />
                  </div>
                )}

                {/* Slot 5 — After 6th project: compact slider (medium) */}
                {(index + 1) % 6 === 0 && (index + 1) % 9 !== 0 && (
                  <div className="lg:col-span-2" key={`ad-compact-${index}`}>
                    <AdSlider placementSlot="explore_inline_mid" variant="compact" />
                  </div>
                )}

                {/* Slot 6 — After 9th project: pill ad (slim) */}
                {(index + 1) % 9 === 0 && (
                  <div className="lg:col-span-2" key={`ad-pill-${index}`}>
                    <MiniAdCard placementSlot="explore_inline_pill" variant="pill" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {showSignInModal && (
        <SignInPromptModal
          onClose={() => setShowSignInModal(false)}
          action="read more about this project"
        />
      )}
    </div>
  );
}
